import Big from 'big.js';
import { DefaultCreditCardDelimiter, formatDate, getCreditCardType, registerCursorTracker } from 'cleave-zen';
import axios from 'smg/axios';
import { GetCountry, GetState, Selects } from 'smg/countries';
import * as paymentoptions from 'smg/customer-api/paymentoptions';
import { GetPaymentOptions } from 'smg/customer-api/paymentoptions';
import { CreateOrder } from 'smg/customer-api/paypal';
import { Session } from 'smg/customer-api/session';
import { UserDetails } from 'smg/customer-api/users';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { FromString, IDsToClasses } from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { Phone } from 'smg/phone';
import { Clone } from 'smg/template';
import { Success } from 'smg/toast';
import { CapitalizeFirstLetter, DollarFormat } from 'smg/util';
import { FormatAddressOneLine } from './address';

interface myWindow extends Window {
    readonly staxOmniWebPaymentsToken: string;
}

declare const window: myWindow;

export class BillingAddressForm {
    $fullName: HTMLInputElement;
    $company: HTMLInputElement;
    $country: HTMLSelectElement;
    $addressLine1: HTMLInputElement;
    $addressLine2: HTMLInputElement;
    $city: HTMLInputElement;
    $state: HTMLSelectElement;
    $zipCode: HTMLInputElement;
    $email: HTMLInputElement;
    $phone: HTMLInputElement;

    private phoneIti: Phone;

    FullName: string = '';
    Company: string = '';
    Country: models.Country | undefined;
    CountryID: models.CountryID | undefined;
    AddressLine1: string = '';
    AddressLine2: string = '';
    City: string = '';
    State: models.State | undefined;
    StateID: models.StateID | undefined;
    Zip: string = '';
    Email: string = '';
    Phone: string = '';

    Set = (addr: models.Address) => {
        this.FullName = addr.fullName || '';
        this.Company = addr.company || '';
        this.AddressLine1 = addr.addressLine1 || '';
        this.AddressLine2 = addr.addressLine2 || '';
        this.City = addr.city || '';
        this.Zip = addr.zip || '';
        this.Email = addr.email || '';
        this.Phone = addr.phone || '';

        this.Country = addr.country;
        this.CountryID = addr.country?.id || addr.countryID;

        this.State = addr.state;
        this.StateID = addr.state?.id || addr.stateID;

        this.$fullName.value = this.FullName;
        this.$company.value = this.Company;
        this.$addressLine1.value = this.AddressLine1;
        this.$addressLine2.value = this.AddressLine2;
        this.$city.value = this.City;
        this.$zipCode.value = this.Zip;
        this.$email.value = this.Email;
        this.$phone.value = this.Phone;

        if (this.CountryID) {
            this.$country.value = this.CountryID.toString();
            this.$country.dispatchEvent(new Event('change'));
        }
        if (this.StateID) {
            this.$state.value = this.StateID.toString();
        }

        setTimeout(async () => {
            await this.phoneIti.Iti.promise.then(() => {
                this.phoneIti.Iti.setNumber(this.Phone);
            });
        });
    };

    constructor($el?: HTMLElement) {
        this.$fullName = $el?.querySelector<HTMLInputElement>('#new-billing-address-full-name') as HTMLInputElement;
        this.$company = $el?.querySelector<HTMLInputElement>('#new-billing-address-company') as HTMLInputElement;
        this.$country = $el?.querySelector<HTMLSelectElement>('#new-billing-address-country') as HTMLSelectElement;
        this.$addressLine1 = $el?.querySelector<HTMLInputElement>('#new-billing-address-line-1') as HTMLInputElement;
        this.$addressLine2 = $el?.querySelector<HTMLInputElement>('#new-billing-address-line-2') as HTMLInputElement;
        this.$city = $el?.querySelector<HTMLInputElement>('#new-billing-address-city') as HTMLInputElement;
        this.$state = $el?.querySelector<HTMLSelectElement>('#new-billing-address-state') as HTMLSelectElement;
        this.$zipCode = $el?.querySelector<HTMLInputElement>('#new-billing-address-zip') as HTMLInputElement;
        this.$email = $el?.querySelector<HTMLInputElement>('#new-billing-address-email') as HTMLInputElement;
        this.$phone = $el?.querySelector<HTMLInputElement>('#new-billing-address-phone') as HTMLInputElement;

        this.phoneIti = new Phone(this.$phone);

        Selects(this.$country, this.$state, {
            defaultUnitedStates: true,
            stateParentSelector: 'div',
            phone: this.phoneIti,
            onStateHide: () => this.$zipCode.parentElement?.classList.replace('col-3', 'col-6'),
            onStateShow: () => this.$zipCode.parentElement?.classList.replace('col-6', 'col-3'),
        }).catch(err => HandleRequestError(err));

        this.$fullName.addEventListener('input', () => {
            this.FullName = this.$fullName.value;
        });
        this.$company.addEventListener('input', () => this.Company = this.$company.value);
        this.$addressLine1.addEventListener('input', () => this.AddressLine1 = this.$addressLine1.value);
        this.$addressLine2.addEventListener('input', () => this.AddressLine2 = this.$addressLine2.value);
        this.$city.addEventListener('input', () => this.City = this.$city.value);
        this.$zipCode.addEventListener('input', () => this.Zip = this.$zipCode.value);
        this.$email.addEventListener('input', () => this.Email = this.$email.value);

        this.$phone.addEventListener('input', () => this.Phone = this.phoneIti.Iti.getNumber());

        this.$country.addEventListener('change', async () => {
            this.Country = await GetCountry(parseInt(this.$country.value, 10));
            this.CountryID = this.Country?.id;

            this.State = await GetState(parseInt(this.$country.value, 10), parseInt(this.$state.value, 10));
            this.StateID = this.State?.id;
        });

        this.$state.addEventListener('change', async () => {
            this.State = await GetState(parseInt(this.$country.value, 10), parseInt(this.$state.value, 10));
            this.StateID = this.State?.id;
        });
    }
}

let userDetails: UserDetails;
let openModal: Modal | undefined;

export async function MakePayment(user: models.User, order: models.Order) {
    const paymentOptions = (await GetPaymentOptions({
        'data': 'fullName,company,addressLine1,addressLine2,city,stateID,zip,countryID,phone,email,creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID',
        'q': 'valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
        'sort': 'created desc',
    }).catch(() => [])).filter(paymentOption => paymentOption.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO);

    if (!paymentOptions.length) {
        openModal?.Close();
        return promptNewPaymentOption(user, order);
    } else {
        const $body = Clone('#payment-option-select-form').firstElementChild?.querySelector('#payment-options-container') as HTMLFormElement;

        if (user.balance && Big(user.balance).gt('0')) {
            $body.appendChild(drawAccountBalanceOption(Big(user.balance)));
        }

        for (const paymentOption of paymentOptions) {
            const $card = drawPaymentOption(paymentOption);
            $body.appendChild(await $card);
        }

        const $m = new Modal({
            title: 'Select Payment Method',
            size: 'modal-sm',
            headerLevel: 'h2',
            confirmText: 'Continue',
            cancelButtonClass: 'd-none',
            body: $body,
            modalDialogScrollable: true,
            onSubmit: () => {
                const $selected = $m.Element.querySelector('input:checked') as HTMLInputElement;
                if (!ReportValidity($m.Element.querySelector('form') as HTMLFormElement)) {
                    return;
                }
                if ($selected?.value === 'payment-option-balance-input') {
                    void paymentAmount(user, order, {
                        paymentOptionTypeID: models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE,
                    }, Big(order.balance || ''));
                } else if ($selected) {
                    void paymentAmount(user, order, paymentOptions.find(po => po.id === $selected.id) as models.PaymentOption, Big(order.balance || ''));
                }
            },
        });

        openModal?.Close();
        openModal = $m;

        const $modalFooter = $m.Element.querySelector('.modal-footer') as HTMLElement;
        const $newPaymentMethodBtn = document.createElement('button');
        $newPaymentMethodBtn.classList.add('btn', 'btn-outline-primary', 'fw-600');
        $newPaymentMethodBtn.innerHTML = '<i class="fas fa-plus"></i>  Use a Different Payment';
        $newPaymentMethodBtn.addEventListener('click', e => {
            e.preventDefault();
            $m.Close();
            promptNewPaymentOption(user, order);
        });
        if ($modalFooter.firstElementChild) {
            $modalFooter.insertBefore($newPaymentMethodBtn, $modalFooter.firstElementChild);
        }
    }
}

function promptNewPaymentOption(user: models.User, order: models.Order) {
    openModal?.Close();

    const $body = Clone('#payment-option-select-form').firstElementChild as HTMLFormElement;
    const $fieldset = $body.querySelector('#payment-option-fieldset') as HTMLFieldSetElement;
    const $optionsContainer = $body.querySelector('#payment-options-container') as HTMLDivElement;

    const $newPaymentOptionTemplate = Clone('#payment-option-form-new-payment-modal').firstElementChild as HTMLElement;
    $optionsContainer.appendChild($newPaymentOptionTemplate);
    const $m = new Modal({
        title: 'New Payment Option',
        size: 'modal-lg',
        headerLevel: 'h2',
        body: $body,
        cancelButtonClass: 'd-none',
        cancelText: 'Back',
        onSubmit: () => {
            const $selectedPaymentOption = $fieldset.querySelector('input:checked') as HTMLInputElement;
            const paymentType = $selectedPaymentOption?.getAttribute('data-payment-type');
            if (paymentType === 'paypal') {
                void paymentAmount(user, order, { paymentOptionTypeID: models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL }, Big(order.balance || ''), () => promptNewPaymentOption(user, order));
                return;
            }
            if (paymentType) {
                newPaymentOptionForm(user, order, paymentType);
            }
        },
    });

    openModal = $m;

    if (openModal) {
        backBtn($m, () => MakePayment(user, order));
    }
}

function newPaymentOptionForm(user: models.User, order: models.Order, paymentType: string) {
    let typeID: models.PaymentOptionTypeID | undefined;
    switch (paymentType) {
    case 'credit-debit':
        typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
        break;
    case 'paypal':
        typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
        break;
    case 'bank':
        typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK;
        break;
    }

    let $body: HTMLElement | undefined;
    let $formBody: HTMLElement | undefined;
    let $billingAddressForm: BillingAddressForm | undefined;

    switch (typeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
        [$body, $formBody] = createCCBody();
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
        [$body, $formBody] = createBankBody();
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL: {
        break;
    }
    }

    function createCCBody(): [HTMLElement, HTMLElement] {
        const $ccBody = Clone('#payment-option-form-new-cc');
        const $ccFormBody = $ccBody.querySelector('.payment-option-form-body') as HTMLElement;
        const $billingShippingSame = $ccBody.querySelector<HTMLInputElement>('#billing-shipping-same');
        const $billingForm = $ccBody.querySelector<HTMLElement>('#billing-address-form');
        if ($billingShippingSame) {
            $billingShippingSame.parentElement?.classList.add('d-none');
        }
        if ($billingForm) {
            $billingForm.classList.remove('collapse');
        }

        if ($billingForm) {
            $billingAddressForm = new BillingAddressForm($billingForm);
        }

        $ccFormBody?.querySelectorAll<HTMLInputElement>('.cc-exp-input').forEach($exp => {
            registerCursorTracker({ input: $exp, delimiter: DefaultCreditCardDelimiter });
            $exp.addEventListener('input', () => {
                const newVal = formatDate($exp.value, {
                    datePattern: ['m', 'y'],
                });
                $exp.value = newVal;
            });
        });
        return [$ccBody, $ccFormBody];
    }

    function createBankBody(): [HTMLElement, HTMLElement] {
        const $bankBody = Clone('#payment-option-form-new-bank-wide');
        const $bankFormBody = $bankBody.querySelector('.payment-option-form-body') as HTMLElement;
        const $billingShippingSame = $bankBody.querySelector<HTMLInputElement>('#billing-shipping-same');
        const $billingForm = $bankBody.querySelector<HTMLElement>('#billing-address-form');
        if ($billingShippingSame) {
            $billingShippingSame.parentElement?.classList.add('d-none');
        }
        if ($billingForm) {
            $billingForm.classList.remove('collapse');
        }

        if ($billingForm) {
            $billingAddressForm = new BillingAddressForm($billingForm);
        }
        return [$bankBody, $bankFormBody];
    }

    const $poSelectParent = $body?.querySelector<HTMLDivElement>('#po-type-select') as HTMLDivElement;
    IDsToClasses($poSelectParent);
    const $poSelect = $poSelectParent?.querySelector<HTMLSelectElement>('.po-type') as HTMLSelectElement;
    $poSelect.appendChild(FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT}">Credit/Debit Card</option>`));
    $poSelect.appendChild(FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL}">Paypal</option>`));
    $poSelect.appendChild(FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK}">Bank Account</option>`));

    if (typeID) {
        $poSelect.value = typeID.toString();
    }

    let phone: Phone | undefined;
    if ($billingAddressForm?.$phone) {
        phone = new Phone($billingAddressForm?.$phone);
    }

    let paymentOption: models.PaymentOption = {};
    let creditCardNumber: string | undefined;
    let bankAccount: string | undefined;
    let bankRouting: string | undefined;
    let accountType: 'checking' | 'savings' | undefined;
    let bankType: 'personal' | 'business' | undefined;
    let securityCode: string | undefined;
    let expireMonth: number | undefined;
    let expireYear: number | undefined;

    const $m = new Modal({
        title: 'New Payment Option',
        size: 'modal-lg',
        headerLevel: 'h2',
        body: $body,
        cancelButtonClass: 'd-none',
        preSubmit: async () => {
            userDetails = {
                fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || $billingAddressForm?.FullName,
                company: user.company || $billingAddressForm?.Company,
                phone: user.phone || await phone?.Format() || '',
                email: user.email || $billingAddressForm?.Email || '',
                countryID: user.country?.id || user.countryID || $billingAddressForm?.Country?.id || $billingAddressForm?.CountryID,
                stateID: user.state?.id || user.stateID || $billingAddressForm?.State?.id || $billingAddressForm?.StateID,
            };
            paymentOption = {
                paymentOptionTypeID: typeID,
                fullName: $billingAddressForm?.FullName,
                company: $billingAddressForm?.Company,
                country: $billingAddressForm?.Country,
                countryID: $billingAddressForm?.CountryID,
                addressLine1: $billingAddressForm?.AddressLine1,
                addressLine2: $billingAddressForm?.AddressLine2,
                city: $billingAddressForm?.City,
                state: $billingAddressForm?.State,
                stateID: $billingAddressForm?.StateID,
                zip: $billingAddressForm?.Zip,
                email: $billingAddressForm?.Email,
                phone: $billingAddressForm?.Phone,
            };

            creditCardNumber = $formBody?.querySelector<HTMLInputElement>('#cc-number')?.value;
            securityCode = $formBody?.querySelector<HTMLInputElement>('#cc-cvc')?.value;
            bankAccount = $formBody?.querySelector<HTMLInputElement>('#bank-acct')?.value;
            bankRouting = $formBody?.querySelector<HTMLInputElement>('#bank-routing')?.value;
            accountType = $formBody?.querySelector<HTMLInputElement>('#bank-acct-checking')?.checked ? 'checking' : 'savings';
            bankType = $formBody?.querySelector<HTMLInputElement>('#bank-type-personal')?.checked ? 'personal' : 'business';

            const $ccExpiration = $formBody?.querySelector<HTMLInputElement>('#cc-expiration');
            const val = $ccExpiration?.value;
            if (val) {
                const [monthStr, yearStr] = val.split('/');
                const month = parseInt(monthStr, 10);
                const year = parseInt(yearStr, 10);
                const currentYear = parseInt(new Date().getFullYear().toString().slice(2), 10);
                if (val.length >= 5) {
                    if ((1 <= month && month <= 12) && (year >= currentYear)) {
                        if (year === currentYear && month < new Date().getMonth() + 1) {
                            $ccExpiration.setCustomValidity('Invalid expiration date');
                        } else {
                            $ccExpiration.setCustomValidity('');
                        }
                    } else {
                        $ccExpiration.setCustomValidity('Invalid expiration date');
                    }
                }
                ReportValidity($ccExpiration);
                expireMonth = month;
                expireYear = year;
            }
        },
        onSubmit: async () => {
            if (typeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                return paymentAmount(user, order, { paymentOptionTypeID: models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL }, Big(order.balance || ''), () => newPaymentOptionForm(user, order, paymentType));
            } else {
                return paymentoptions.NewPaymentOption({
                    user: userDetails,
                    paymentOption,
                    creditCardNumber,
                    expireMonth,
                    expireYear,
                    securityCode,
                    bankAccount,
                    bankRouting,
                    accountType,
                    bankType,
                    fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
                }).then(paymentOption => {
                    Success('Your payment method has been added to your account.');
                    void paymentAmount(user, order, paymentOption, Big(order.balance || ''), () => newPaymentOptionForm(user, order, paymentType));
                });
            }
        },
    });

    openModal?.Close();
    openModal = $m;

    backBtn(($m as Modal), () => promptNewPaymentOption(user, order));

    $poSelect.addEventListener('change', () => {
        const $modalBody = ($m as Modal).Element.querySelector('.modal-body') as HTMLElement;
        const $modalFormBody = $modalBody.querySelector('.payment-option-form-body') as HTMLElement;
        switch (parseInt($poSelect.value, 10)) {
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
            [$body, $formBody] = createCCBody();
            $modalFormBody.innerHTML = '';
            $modalFormBody.appendChild($formBody);
            break;
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK;
            [$body, $formBody] = createBankBody();
            $modalFormBody.innerHTML = '';
            $modalFormBody.appendChild($formBody);
            break;
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
            $formBody = Clone('#payment-option-form-paypal').querySelector('.payment-option-form-body') as HTMLElement;
            $modalFormBody.innerHTML = '';
            $modalFormBody.appendChild($formBody);
            break;
        default:
            console.error('Unknown payment option type selected');
            return;
        }
    });
}

async function paymentAmount(user: models.User, order: models.Order, paymentOption: models.PaymentOption, paymentAmount: Big, prevFunc?: () => void) {
    const body = Clone('#payment-amount-modal-form-template');
    let $card: HTMLElement | undefined;

    (body.querySelector('#modal-balance-amount') as HTMLElement).textContent = DollarFormat(paymentAmount);
    const defaultRadio = body.querySelector('#modal-payment-amount-given') as HTMLInputElement;
    const customRadio = body.querySelector('#modal-payment-amount-custom') as HTMLInputElement;
    const customAmountParent = body.querySelector('#modal-custom-amount-parent') as HTMLElement;
    const customAmount = body.querySelector('#modal-custom-amount') as HTMLInputElement;
    customAmount.placeholder = paymentAmount.toFixed(2);
    customAmount.max = paymentAmount.toString();

    customRadio.addEventListener('click', () => {
        customAmountParent.classList.remove('d-none');
        customAmount.required = true;
        setTimeout(() => customAmount.focus());
    });
    defaultRadio.addEventListener('click', () => {
        customAmountParent.classList.add('d-none');
        customAmount.required = false;
    });

    if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
        $card = Clone('#payment-option-form-paypal').querySelector('.payment-option-form-body') as HTMLElement;
    } else if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE) {
        $card = Clone('#payment-option-form-balance').querySelector('.payment-option-form-body') as HTMLElement;
        const $balanceAmount = $card.querySelector('.balance-amount') as HTMLElement;
        $balanceAmount.textContent = DollarFormat(Big(user.balance || 0));

        if (Big(user.balance || 0).lt(paymentAmount)) {
            defaultRadio.disabled = true;
            customAmount.placeholder = Big(user.balance || 0).toFixed(2);
            customRadio.checked = true;
            customRadio.dispatchEvent(new Event('click'));
            customAmount.value = Big(user.balance || 0).toFixed(2);
            customRadio.parentElement?.classList.add('mt-4');
        }
    } else {
        await drawPaymentOption(paymentOption).then(card => {
            $card = card;
        });
    }

    if ($card) {
        $card.querySelector('input')?.remove();
        body.querySelector('#modal-payment-option')?.replaceWith($card);
    }

    const $m = new Modal({
        title: 'Confirm Payment',
        outsideClosable: false,
        headerLevel: 'h2',
        confirmText: 'Confirm Payment',
        cancelButtonClass: 'd-none',
        body,
        onSubmit: async () => {
            if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                const payPalRedirectURL = await CreateOrder({
                    userDetails: userDetails,
                    orderIDs: [order.id || ''],
                    successURL: window.location.href,
                    cancelURL: window.location.href,
                    amount: customRadio.checked ? customAmount.value : paymentAmount.round(2)?.toString(),
                });

                window.location.href = payPalRedirectURL || '';
            } else if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE) {
                const balancePaymentOption = await getBalancePaymentOption();
                await axios.post<models.OrderPayment[]>('/order-payments', {
                    'user': user,
                    'orderIDs': [order.id],
                    'paymentOptionID': balancePaymentOption.id,
                    'amount': customRadio.checked ? customAmount.value : paymentAmount.round(2)?.toString(),
                }).then(() => {
                    window.location.reload();
                    Success('Your payment has been received.');
                });
            } else {
                await axios.post<models.OrderPayment[]>('/order-payments', {
                    'user': user,
                    'orderIDs': [order.id],
                    'paymentOptionID': paymentOption.key,
                    'amount': customRadio.checked ? customAmount.value : paymentAmount.round(2)?.toString(),

                }).then(() => {
                    window.location.reload();
                    Success('Your payment has been received.');
                });
            }
        },
    });

    openModal = $m;
    backBtn($m, () => prevFunc ? prevFunc() : MakePayment(user, order));
}

async function getBalancePaymentOption(): Promise<models.PaymentOption> {
    const paymentOptions = await paymentoptions.GetPaymentOptions({
        'data': 'paymentOptionTypeID',
        'q': `Type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE}`,
        'sort': 'created desc',
        'max': 1,
    });

    if (paymentOptions.length) {
        return paymentOptions[0];
    }

    const sess = await Session();

    const user: UserDetails = {
        firstName: sess.firstName,
        lastName: sess.lastName,
        company: sess.company,
        email: sess.email,
        phone: sess.phone,
    };

    return await axios.post<models.PaymentOption>('/payment-options', {
        'user': user || userDetails,
        'paymentOptionTypeID': models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE,
    }).then(resp => resp.data);
}

function backBtn($m: Modal, callBack: () => void) {
    const $modalFooter = $m.Element.querySelector('.modal-footer') as HTMLElement;
    const $backBtn = document.createElement('button');
    $backBtn.classList.add('btn', 'btn-outline-primary');
    $backBtn.textContent = 'Back';
    $backBtn.addEventListener('click', e => {
        e.preventDefault();
        callBack();
    });
    if ($modalFooter.firstElementChild) {
        $modalFooter.insertBefore($backBtn, $modalFooter.firstElementChild);
    }
}

// Utility/Draw functions

async function drawPaymentOption(paymentOption: models.PaymentOption): Promise<HTMLElement> {
    if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
        const $el = Clone('#payment-option-card-bank');
        const $input = $el.querySelector<HTMLInputElement>('input');
        const $label = $el.querySelector<HTMLLabelElement>('label');
        if ($input && $label && paymentOption.id) {
            $input.value = paymentOption.id;
            $input.id = paymentOption.id;
            $input.name = 'payment-option-select';
            $label.htmlFor = paymentOption.id;
        }
        const bankType = $el.querySelector('.bank-acct-type') as HTMLElement;
        if (paymentOption.bankType && paymentOption.accountType) {
            bankType.textContent = `${CapitalizeFirstLetter(paymentOption.bankType)} ${CapitalizeFirstLetter(paymentOption.accountType)} Account`;
            if (paymentOption.creditCardLast4) {
                bankType.textContent += ` (...${paymentOption.creditCardLast4})`;
            }
        }
        const poAddress: models.ShippingOption = {
            city: paymentOption.city,
            state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
            zip: paymentOption.zip,
            country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
        };
        const $address = $el.querySelector('.bank-address') as HTMLElement;
        $address.innerHTML = await FormatAddressOneLine(poAddress);
        $el.querySelector('.controls')?.remove();
        return $el;
    } else if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
        const $el = Clone('#payment-option-card-cc');
        const $input = $el.querySelector<HTMLInputElement>('input');
        const $label = $el.querySelector<HTMLLabelElement>('label');
        if ($input && $label && paymentOption.id) {
            $input.value = paymentOption.id;
            $input.id = paymentOption.id;
            $input.name = 'payment-option-select';
            $label.htmlFor = paymentOption.id;
        }
        const $ccLastFour = $el.querySelector('.cc-last-four') as HTMLElement;
        const $ccIssuer = $el.querySelector('.cc-issuer') as HTMLElement;
        const poAddress: models.ShippingOption = {
            city: paymentOption.city,
            state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
            zip: paymentOption.zip,
            country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
        };
        const $address = $el.querySelector('.cc-address') as HTMLElement;
        $address.innerHTML = await FormatAddressOneLine(poAddress);
        if (paymentOption.creditCardLast4) {
            $ccLastFour.textContent = paymentOption.creditCardLast4.toString().padStart(4, '0');
        }
        if (paymentOption.creditCardFirst6) {
            switch (getCreditCardType(paymentOption.creditCardFirst6.toString()).toString()) {
            case 'amex':
                $ccIssuer.textContent = 'Amex';
                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                $el.querySelector('.cc-icon-amex')?.classList.remove('d-none');
                break;
            case 'discover':
                $ccIssuer.textContent = 'Discover';
                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                $el.querySelector('.cc-icon-discover')?.classList.remove('d-none');
                break;
            case 'mastercard':
                $ccIssuer.textContent = 'Mastercard';
                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                $el.querySelector('.cc-icon-mastercard')?.classList.remove('d-none');
                break;
            case 'visa':
                $ccIssuer.textContent = 'Visa';
                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                $el.querySelector('.cc-icon-visa')?.classList.remove('d-none');
                break;
            }
        }
        $el.querySelector('.controls')?.remove();
        return $el;
    }
    return Promise.reject(new Error('Unknown payment option type'));
}

function drawAccountBalanceOption(balance: Big): HTMLElement {
    const $el = Clone('#payment-option-balance');
    const $input = $el.querySelector<HTMLInputElement>('input');
    const $label = $el.querySelector<HTMLLabelElement>('label');
    if ($input && $label) {
        $input.value = 'payment-option-balance-input';
        $input.id = 'payment-option-balance-input';
        $input.name = 'payment-option-select';
        $label.htmlFor = 'payment-option-balance-input';
    }
    const $balance = $el.querySelector('.amount') as HTMLElement;
    $balance.textContent = DollarFormat(balance);
    return $el;
}